import React, { useState, useCallback } from 'react';
import { Upload, FileSpreadsheet, FileText, FilePlus, X } from 'lucide-react';
import type { AcquisitionSourceFormat } from '../../types/acquisitionAnalysis';
import { Spinner } from '../common/Spinner';
import {
  SOURCE_FORMATS,
  getSourceFormat,
  isFileAllowedForSource,
} from './sourceFormats';

interface AnalysisUploaderProps {
  onStartAnalysis: (
    targetName: string,
    files: File[],
    fiscalYearStart: number,
    sourceFormat: AcquisitionSourceFormat
  ) => Promise<void>;
  isLoading: boolean;
}

const MONTHS = [
  { value: 1, label: 'January' },
  { value: 2, label: 'February' },
  { value: 3, label: 'March' },
  { value: 4, label: 'April' },
  { value: 5, label: 'May' },
  { value: 6, label: 'June' },
  { value: 7, label: 'July' },
  { value: 8, label: 'August' },
  { value: 9, label: 'September' },
  { value: 10, label: 'October' },
  { value: 11, label: 'November' },
  { value: 12, label: 'December' },
];

const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB per file
/**
 * Aggregate-payload cap. Files are base64-encoded into the Firebase callable
 * request body, which has a hard 10 MB limit (Firebase HTTPS callable spec).
 * Base64 is ~33% larger than the binary, so we cap raw bytes at 7 MB to leave
 * headroom for JSON envelope overhead. 25 × 1-2KB Connective PDFs (≈50KB total)
 * sail under this; 5 × 5MB Loan Market PDFs would silently bust 10MB without it.
 */
const MAX_AGGREGATE_RAW_BYTES = 7 * 1024 * 1024;
const ACCEPTED_FILE_TYPES = [
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // xlsx
  'application/vnd.ms-excel', // xls
  'text/csv',
  'application/pdf',
];

const formatBytes = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
};

export const AnalysisUploader: React.FC<AnalysisUploaderProps> = ({
  onStartAnalysis,
  isLoading,
}) => {
  const [targetName, setTargetName] = useState('');
  const [fiscalYearStart, setFiscalYearStart] = useState(7); // Default: July
  const [files, setFiles] = useState<File[]>([]);
  const [dragActive, setDragActive] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);
  const [sourceFormat, setSourceFormat] =
    useState<AcquisitionSourceFormat>('auto');

  const sourceCfg = getSourceFormat(sourceFormat);

  const validateFile = (file: File): string | null => {
    if (file.size > MAX_FILE_SIZE) {
      return `${file.name}: File size exceeds 50MB limit`;
    }

    if (
      !ACCEPTED_FILE_TYPES.includes(file.type) &&
      !file.name.match(/\.(xlsx?|csv|pdf)$/i)
    ) {
      return `${file.name}: Invalid file type. Only Excel, CSV, and PDF files are accepted`;
    }

    // Per-source-format extension narrowing (skip when 'auto')
    if (sourceFormat !== 'auto' && !isFileAllowedForSource(file.name, sourceFormat)) {
      return `${file.name}: ${sourceCfg.label} expects ${sourceCfg.acceptedExtensions.join(' / ')} files`;
    }

    return null;
  };

  const handleFiles = useCallback((newFiles: FileList | null) => {
    if (!newFiles) return;

    const validationErrors: string[] = [];
    const validFiles: File[] = [];

    Array.from(newFiles).forEach(file => {
      const error = validateFile(file);
      if (error) {
        validationErrors.push(error);
      } else {
        validFiles.push(file);
      }
    });

    setErrors(validationErrors);
    setFiles(prev => [...prev, ...validFiles]);
  }, []);

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    handleFiles(e.dataTransfer.files);
  }, [handleFiles]);

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleFiles(e.target.files);
  };

  const removeFile = (index: number) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const totalRawBytes = files.reduce((acc, f) => acc + f.size, 0);
  const exceedsAggregateCap = totalRawBytes > MAX_AGGREGATE_RAW_BYTES;

  const handleSubmit = async () => {
    const validationErrors: string[] = [];

    if (!targetName.trim()) {
      validationErrors.push('Target name is required');
    }

    if (files.length === 0) {
      validationErrors.push('At least one file is required');
    }

    if (exceedsAggregateCap) {
      validationErrors.push(
        `Total upload size ${formatBytes(totalRawBytes)} exceeds ${formatBytes(MAX_AGGREGATE_RAW_BYTES)} cap. Firebase callable bodies are limited to 10 MB after base64 encoding; remove files or split into multiple analyses.`
      );
    }

    if (validationErrors.length > 0) {
      setErrors(validationErrors);
      return;
    }

    setErrors([]);
    await onStartAnalysis(targetName, files, fiscalYearStart, sourceFormat);
  };

  const getFileIcon = (file: File) => {
    if (file.type.includes('pdf')) {
      return <FileText className="w-5 h-5 text-red-400" />;
    }
    return <FileSpreadsheet className="w-5 h-5 text-green-400" />;
  };

  return (
    <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700/50 overflow-hidden">
      <div className="px-6 py-5 border-b border-slate-700/50">
        <h2 className="text-xl font-semibold text-white">Start New Analysis</h2>
        <p className="text-sm text-slate-400 mt-1">
          Upload commission data and RCTI files to generate a comprehensive trail book valuation
        </p>
      </div>

      <div className="p-6 space-y-6">
        {/* Target Name Input */}
        <div>
          <label className="block text-sm font-medium text-slate-300 mb-2">
            Target Company Name *
          </label>
          <input
            type="text"
            value={targetName}
            onChange={(e) => setTargetName(e.target.value)}
            placeholder="e.g., Entourage Mortgage Group"
            className="w-full px-4 py-2.5 bg-slate-900/50 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
            disabled={isLoading}
          />
        </div>

        {/* Fiscal Year Start Selector */}
        <div>
          <label className="block text-sm font-medium text-slate-300 mb-2">
            Fiscal Year Start Month
          </label>
          <select
            value={fiscalYearStart}
            onChange={(e) => setFiscalYearStart(Number(e.target.value))}
            className="w-full px-4 py-2.5 bg-slate-900/50 border border-slate-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
            disabled={isLoading}
          >
            {MONTHS.map(month => (
              <option key={month.value} value={month.value}>
                {month.label}
              </option>
            ))}
          </select>
          <p className="text-xs text-slate-500 mt-1">
            Default is July (Australian fiscal year)
          </p>
        </div>

        {/* Source-format selector */}
        <div>
          <label className="block text-sm font-medium text-slate-300 mb-2">
            Source Format
          </label>
          <select
            value={sourceFormat}
            onChange={(e) =>
              setSourceFormat(e.target.value as AcquisitionSourceFormat)
            }
            className="w-full px-4 py-2.5 bg-slate-900/50 border border-slate-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
            disabled={isLoading}
          >
            {SOURCE_FORMATS.filter((s) => s.supported).map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </select>
          <p className="text-xs text-slate-500 mt-1">
            {sourceCfg.description}
          </p>
          {sourceFormat !== 'auto' && !sourceCfg.hasBrokerLevelData && (
            <div className="mt-2 px-3 py-2 bg-amber-500/10 border border-amber-500/30 rounded text-xs text-amber-300">
              <strong>Note:</strong> Entity-level inputs do not contain
              per-broker rows. Top Brokers, Concentration, Retention, and
              Growth Trajectory tabs will be hidden in the analysis report.
            </div>
          )}
        </div>

        {/* File Upload Area */}
        <div>
          <label className="block text-sm font-medium text-slate-300 mb-2">
            Upload Files *
          </label>
          <div
            className={`relative border-2 border-dashed rounded-lg p-8 text-center transition-all ${
              dragActive
                ? 'border-indigo-500 bg-indigo-500/10'
                : 'border-slate-700 hover:border-slate-600 bg-slate-900/30'
            }`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
          >
            <input
              type="file"
              multiple
              accept={sourceCfg.acceptedExtensions.join(',')}
              onChange={handleFileInput}
              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
              disabled={isLoading}
            />
            <Upload className="w-12 h-12 text-slate-500 mx-auto mb-3" />
            <p className="text-slate-300 font-medium mb-1">
              Drop files here or click to browse
            </p>
            <p className="text-sm text-slate-500">
              Accepts Excel (.xlsx, .xls), CSV, and PDF files
            </p>
            <p className="text-xs text-slate-600 mt-2">
              Maximum file size: 50MB
            </p>
          </div>
        </div>

        {/* File List */}
        {files.length > 0 && (
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <label className="block text-sm font-medium text-slate-300">
                Selected Files ({files.length})
              </label>
              <span
                className={`text-xs font-mono ${
                  exceedsAggregateCap ? 'text-red-400' : 'text-slate-400'
                }`}
              >
                {formatBytes(totalRawBytes)} / {formatBytes(MAX_AGGREGATE_RAW_BYTES)} total
              </span>
            </div>
            {files.map((file, index) => (
              <div
                key={index}
                className="flex items-center justify-between p-3 bg-slate-900/50 border border-slate-700 rounded-lg"
              >
                <div className="flex items-center gap-3">
                  {getFileIcon(file)}
                  <div>
                    <p className="text-sm text-white font-medium">{file.name}</p>
                    <p className="text-xs text-slate-500">
                      {(file.size / 1024 / 1024).toFixed(2)} MB
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => removeFile(index)}
                  className="p-1 hover:bg-slate-700 rounded transition-colors"
                  disabled={isLoading}
                >
                  <X className="w-4 h-4 text-slate-400" />
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Errors */}
        {errors.length > 0 && (
          <div className="p-4 bg-red-500/20 border border-red-500/30 rounded-lg">
            <ul className="space-y-1">
              {errors.map((error, index) => (
                <li key={index} className="text-sm text-red-400">
                  {error}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Start Analysis Button */}
        <button
          onClick={handleSubmit}
          disabled={
            isLoading ||
            files.length === 0 ||
            !targetName.trim() ||
            exceedsAggregateCap
          }
          className="w-full px-6 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white rounded-lg font-semibold transition-all shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          {isLoading ? (
            <>
              <Spinner size="sm" />
              Starting Analysis...
            </>
          ) : (
            <>
              <FilePlus className="w-5 h-5" />
              Start Analysis
            </>
          )}
        </button>
      </div>
    </div>
  );
};
